/**
 * usePersonalizedQuestions.ts - Suggestions built from this user's own chat history
 *
 * Distinct from useSuggestedQuestions, which reads the circle's admin-authored `_widget`
 * post and shows every visitor the same chips. These come from the server, are personal to
 * the signed-in user, and are regenerated only when they start a new chat.
 *
 * The endpoint always answers 200, using `status` to say why a list is empty, so there is no
 * error state to render — and `shouldRetryOnError` is off because a failure here is not worth
 * a second round trip on a welcome screen.
 */

import useSWR from 'swr';
import { useApi } from 'core';
import { useAuthContext } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';

export interface PersonalizedQuestion {
    text: string;
}

interface Response {
    ok?: number;
    questions?: PersonalizedQuestion[];
    cached?: boolean;
    status?: 'ok' | 'no_history' | 'disabled' | 'unavailable';
}

export interface UsePersonalizedQuestionsResult {
    questions: PersonalizedQuestion[];
    isLoading: boolean;
}

/**
 * The SWR key. Exported because the pre-warm writes the same key from outside this hook —
 * if the two ever disagreed the pre-warm would silently fill a cache nobody reads.
 */
export const personalizedQuestionsKey = (
    userId: string | null | undefined,
    circleId: string | null,
    locale: string,
) => (circleId && userId ? ['personalized-questions', userId, circleId, locale] : null);

/** Normalised to the bare language: the stored row is keyed on it, so 'en-GB' must not miss 'en'. */
export const personalizedQuestionsLocale = (language: string | undefined) =>
    (language || 'en').split('-')[0];

export const fetchPersonalizedQuestions = async (
    api: { get: (args: { path: string }) => Promise<any> },
    circleId: string,
    locale: string,
): Promise<PersonalizedQuestion[]> => {
    const response: Response = await api.get({
        path: `circles/${circleId}/suggested-questions/personalized?locale=${locale}`,
    });
    return Array.isArray(response?.questions) ? response.questions : [];
};

export const usePersonalizedQuestions = (circleId: string | null): UsePersonalizedQuestionsResult => {
    const api = useApi();
    const { profile, isAuthenticated } = useAuthContext();
    const userId = isAuthenticated ? profile?._id : null;
    const { i18n } = useTranslation();
    // The server has no reliable way to know the widget's language, so it is sent explicitly
    // and forms part of the stored row's key — switching language regenerates.
    const locale = personalizedQuestionsLocale(i18n.language);

    const { data, isLoading, error } = useSWR(
        personalizedQuestionsKey(userId, circleId, locale),
        async () => fetchPersonalizedQuestions(api, circleId as string, locale),
        {
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            // A miss costs a model call, so never retry on failure and keep the answer for
            // the length of a welcome-screen visit.
            shouldRetryOnError: false,
            dedupingInterval: 60000,
        }
    );

    return {
        questions: (userId && !error ? data || [] : []).filter(q => q && typeof q.text === 'string' && q.text.trim()),
        // SWR reports isLoading on every mount that kicks off a request, INCLUDING one that
        // already has an answer in cache from the pre-warm — reporting that as loading would
        // hide chips we are holding, which is the exact wait the pre-warm exists to remove.
        // Loading here means what the caller needs it to mean: there is nothing to show yet.
        isLoading: isLoading && data === undefined,
    };
};
